import { useEffect, useRef, useState } from 'react';

interface ParallaxBackgroundProps {
  reducedMotion: boolean;
  /** Called when mid layer is visible and particles can start */
  onMidLayerReady?: () => void;
}

const PARALLAX_AMOUNT = 2.5; // Percentage of movement (look-around effect)
const EASING = 0.01; // Lower = more inertia/lag
const MOVEMENT_THRESHOLD = 0.001; // Skip updates below this threshold

// Image URLs for preloading
const LAYER_IMAGES = {
  background: '/assets/background_world_landscape_2.png',
  mid: '/assets/bg-layer-1.png',
} as const;

// Orchestrated timing (ms)
const REVEAL_TIMING = {
  particles: 400, // Particles start after layers visible
} as const;

/**
 * Layered parallax background with mouse-controlled movement
 * Mid layer appears instantly, background blurs in behind like a dream
 *
 * Layers:
 * - Layer 0: Base parallax layer (moves with cursor, fades/blurs in)
 * - Layer 1: Mid layer (appears immediately on top)
 */
export function ParallaxBackground({ reducedMotion, onMidLayerReady }: ParallaxBackgroundProps) {
  const [position, setPosition] = useState({ x: 0.5, y: 0.5 });
  const rafRef = useRef<number>(0);
  const targetRef = useRef({ x: 0.5, y: 0.5 });

  // Track image loading and reveal state
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [revealStage, setRevealStage] = useState<'loading' | 'revealing' | 'complete'>('loading');
  const loadedCountRef = useRef(0);

  // Preload all images before starting reveal
  useEffect(() => {
    const imageUrls = Object.values(LAYER_IMAGES);
    loadedCountRef.current = 0;

    const handleLoad = () => {
      loadedCountRef.current += 1;
      if (loadedCountRef.current === imageUrls.length) {
        setImagesLoaded(true);
      }
    };

    imageUrls.forEach((url) => {
      const img = new Image();
      img.onload = handleLoad;
      img.onerror = handleLoad; // Count errors as loaded to prevent hanging
      img.src = url;
    });
  }, []);

  // Orchestrate reveal sequence after images load
  useEffect(() => {
    if (!imagesLoaded) return;

    // Both layers start immediately
    setRevealStage('revealing');

    // Notify parent for particles after mid layer visible
    const particleTimer = setTimeout(() => {
      onMidLayerReady?.();
    }, REVEAL_TIMING.particles);

    // Mark complete after background animation finishes
    const completeTimer = setTimeout(() => {
      setRevealStage('complete');
    }, 1500);

    return () => {
      clearTimeout(particleTimer);
      clearTimeout(completeTimer);
    };
  }, [imagesLoaded, onMidLayerReady]);

  // Handle mouse movement
  useEffect(() => {
    if (reducedMotion) return;

    const handleMouseMove = (e: MouseEvent) => {
      targetRef.current = {
        x: e.clientX / window.innerWidth,
        y: e.clientY / window.innerHeight,
      };
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [reducedMotion]);

  // Animation loop with inertia (optimized to skip negligible updates)
  useEffect(() => {
    if (reducedMotion) return;

    const animate = () => {
      setPosition((prev) => {
        const dx = targetRef.current.x - prev.x;
        const dy = targetRef.current.y - prev.y;

        // Skip update if movement is negligible
        if (Math.abs(dx) < MOVEMENT_THRESHOLD && Math.abs(dy) < MOVEMENT_THRESHOLD) {
          rafRef.current = requestAnimationFrame(animate);
          return prev;
        }

        rafRef.current = requestAnimationFrame(animate);
        return {
          x: prev.x + dx * EASING,
          y: prev.y + dy * EASING,
        };
      });
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [reducedMotion]);

  // Calculate parallax offset (inverted - image moves opposite to mouse)
  const parallaxX = reducedMotion ? 0 : (0.5 - position.x) * PARALLAX_AMOUNT;
  const parallaxY = reducedMotion ? 0 : (0.5 - position.y) * PARALLAX_AMOUNT;

  // Determine visibility based on reveal stage
  const showLayers = revealStage !== 'loading';

  return (
    <div className="absolute inset-0 overflow-hidden bg-void" style={{ transformStyle: 'preserve-3d' }}>
      {/* Layer 0 - Parallax base layer (fades/blurs in behind mid layer) */}
      <img
        src={LAYER_IMAGES.background}
        alt=""
        aria-hidden="true"
        decoding="async"
        fetchPriority="high"
        className={`absolute inset-0 w-full h-full object-cover object-center ${
          showLayers ? 'animate-bg-fade-in-unblur' : 'opacity-0'
        }`}
        style={{
          transform: `translate3d(${parallaxX}%, ${parallaxY}%, 0) scale(1.1)`,
          willChange: reducedMotion ? 'auto' : 'transform',
          contain: 'layout style paint',
        }}
      />

      {/* Layer 1 - Mid layer (appears immediately on top) */}
      <img
        src={LAYER_IMAGES.mid}
        alt=""
        aria-hidden="true"
        decoding="async"
        fetchPriority="high"
        className={`absolute inset-0 w-full h-full object-cover object-center ${
          showLayers ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          contain: 'layout style paint',
          willChange: 'auto',
        }}
      />
    </div>
  );
}
